import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduleKeys } from './queryKeys';
import * as templateService from '@/services/schedule/templateService';
import { useToast } from '@/hooks/use-toast';

export const templateKeys = {
  all: ['templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (companyId: string | undefined) => [...templateKeys.lists(), companyId] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (id: string) => [...templateKeys.details(), id] as const,
};

/**
 * Hook to fetch all templates for the company
 */
export const useTemplates = (companyId: string | undefined) => {
  return useQuery({
    queryKey: templateKeys.list(companyId),
    queryFn: templateService.fetchCompanyTemplates,
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a new template
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: templateService.createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      toast({
        title: 'Template created',
        description: 'Your shift template has been created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error creating template',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to update an existing template
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<templateService.TemplateInput> }) =>
      templateService.updateTemplate(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      toast({
        title: 'Template updated',
        description: 'Your changes have been saved successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating template',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to delete a template
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: templateService.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      toast({
        title: 'Template deleted',
        description: 'The template has been deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error deleting template',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to apply a template to employees
 */
export const useApplyTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: templateService.applyTemplate,
    onSuccess: (createdCount) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      toast({
        title: 'Template applied',
        description: `Successfully created ${createdCount} shift entries`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error applying template',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to get template preview
 */
export const useTemplatePreview = (
  templateId: string | undefined,
  year: number,
  month: number,
  enabled: boolean = false
) => {
  return useQuery({
    queryKey: [...templateKeys.detail(templateId || ''), 'preview', year, month],
    queryFn: () => templateService.getTemplatePreview(templateId!, year, month),
    enabled: enabled && !!templateId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};
